import React from "react";
/**
 * Routing abstraction for the Import Hub.
 *
 * Import Hub components don't import a router. Instead, callers pass:
 *
 *   - `renderProductLink`: how to render a link to /import/products/:id.
 *     Defaults to a plain <a href>. Override in the host app to use your
 *     router's Link (react-router-dom, TanStack, Next, etc.) for
 *     client-side navigation.
 *
 *   - `onProductCreated`: called after the wizard creates a product.
 *     The host decides whether to navigate, toast, close, etc.
 *
 * This keeps the Import Hub export routing-agnostic — drop it into any
 * app and wire URLs to your existing router.
 */

import type { ReactNode } from "react";

export interface ProductLinkArgs {
  id: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}

export type RenderProductLink = (args: ProductLinkArgs) => ReactNode;

export interface DatabaseLinkArgs {
  dbId: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}

export type RenderDatabaseLink = (args: DatabaseLinkArgs) => ReactNode;

export interface HrefLinkArgs {
  href: string;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
  onClick?: () => void;
}

export type RenderHrefLink = (args: HrefLinkArgs) => ReactNode;

/** Default: plain <a href="/import/products/:id">. Host router-agnostic. */
export const defaultRenderProductLink: RenderProductLink = ({
  id,
  className,
  children,
  onClick,
}) => (
  <a
    href={`/import/products/${encodeURIComponent(id)}`}
    className={className}
    onClick={onClick}
  >
    {children}
  </a>
);

/** Default: plain <a href="/import/db/:dbId">. Host router-agnostic. */
export const defaultRenderDatabaseLink: RenderDatabaseLink = ({
  dbId,
  className,
  children,
  onClick,
}) => (
  <a
    href={`/import/db/${encodeURIComponent(dbId)}`}
    className={className}
    onClick={onClick}
  >
    {children}
  </a>
);

/** Default: plain <a href>. Host router-agnostic. */
export const defaultRenderHrefLink: RenderHrefLink = ({
  href,
  className,
  style,
  children,
  onClick,
}) => (
  <a href={href} className={className} style={style} onClick={onClick}>
    {children}
  </a>
);

